// src/services/photoService.ts
// ✅ LOGGING: Complete migration to centralized logging system

import { logger } from '../utils/logger';

// ✅ LOGGING: Create dedicated logger for Photo Service
const photoServiceLogger = logger.createServiceLogger('PhotoService');

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://oumk71qqyb.execute-api.us-west-2.amazonaws.com/Dev';

export interface PhotoUploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string;
}

export interface PhotoUploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export class PhotoService {
  private tenantId: string;

  constructor(tenantId: string = 'WallaWallaPD') {
    this.tenantId = tenantId;
    photoServiceLogger.debug('PhotoService initialized', { tenantId });
  }

  /**
   * Get presigned URL for direct upload to S3 - Fixed to match working Lambda
   */
  async getPresignedUploadUrl(
    itemId: string, 
    fileName: string, 
    fileType: string
  ): Promise<{ uploadUrl: string; photoKey: string; publicUrl: string }> {
    const timer = logger.startTimer('PhotoService', 'getPresignedUploadUrl');
    
    try {
      photoServiceLogger.info('Requesting presigned upload URL', {
        itemId,
        fileName,
        fileType,
        tenantId: this.tenantId,
        fileSize: 'unknown' // File size not available at this stage
      });
      
      // Send parameters that match your working Lambda
      const response = await fetch(`${API_BASE_URL}/photos/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          itemId: itemId,           // Lambda expects itemId
          fileName: fileName,       // Lambda expects fileName  
          contentType: fileType,    // Lambda expects contentType
          tenantId: this.tenantId   // Lambda expects tenantId
        })
      });

      photoServiceLogger.info('Presigned URL API response received', {
        itemId,
        fileName,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        photoServiceLogger.error('Presigned URL request failed', {
          itemId,
          fileName,
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500) // Limit error text length
        });
        
        timer();
        throw new Error(`Failed to get presigned URL: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      photoServiceLogger.info('Presigned URL obtained successfully', {
        itemId,
        fileName,
        hasPresignedUrl: !!data.presignedUrl,
        hasPhotoUrl: !!data.photoUrl,
        hasKey: !!data.key
      });
      
      timer();
      // Your working Lambda returns { presignedUrl, photoUrl, key }
      return {
        uploadUrl: data.presignedUrl,  // Map presignedUrl to uploadUrl
        photoKey: data.key,
        publicUrl: data.photoUrl       // Map photoUrl to publicUrl
      };
    } catch (error) {
      timer();
      photoServiceLogger.error('Error getting presigned upload URL', {
        itemId,
        fileName,
        fileType,
        tenantId: this.tenantId,
        error: error instanceof Error ? error.message : 'Unknown error'
      }, error instanceof Error ? error : undefined);
      throw error;
    }
  }

  /**
   * Upload file directly to S3 using presigned URL
   */
  private async uploadToS3(
    file: File, 
    uploadUrl: string,
    onProgress?: (progress: PhotoUploadProgress) => void
  ): Promise<PhotoUploadResult> {
    const timer = logger.startTimer('PhotoService', 'uploadToS3');
    
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();

      photoServiceLogger.info('Starting S3 upload', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        uploadUrl: uploadUrl.substring(0, 50) + '...', // Truncate URL for logging
        timestamp: new Date().toISOString()
      });

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100)
          };
          
          photoServiceLogger.debug('S3 upload progress update', {
            fileName: file.name,
            loaded: progress.loaded,
            total: progress.total,
            percentage: progress.percentage,
            bytesRemaining: progress.total - progress.loaded
          });
          
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        photoServiceLogger.info('S3 upload completed', {
          fileName: file.name,
          fileSize: file.size,
          status: xhr.status,
          statusText: xhr.statusText,
          success: xhr.status === 200 || xhr.status === 204
        });
        
        timer();
        if (xhr.status === 200 || xhr.status === 204) {
          resolve({ success: true });
        } else {
          photoServiceLogger.error('S3 upload failed with HTTP error', {
            fileName: file.name,
            status: xhr.status,
            responseText: xhr.responseText?.substring(0, 500) // Limit response text
          });
          resolve({
            success: false,
            error: `Upload failed with status ${xhr.status}`
          });
        }
      });

      xhr.addEventListener('error', () => {
        timer();
        photoServiceLogger.error('S3 upload network error', {
          fileName: file.name,
          fileSize: file.size
        });
        resolve({
          success: false,
          error: 'Network error during upload'
        });
      });

      xhr.addEventListener('abort', () => {
        timer();
        photoServiceLogger.warn('S3 upload aborted', {
          fileName: file.name,
          fileSize: file.size
        });
        resolve({
          success: false,
          error: 'Upload was aborted'
        });
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  /**
   * Validate file before upload
   */
  validateFile(file: File): { valid: boolean; error?: string } {
    photoServiceLogger.debug('Validating file for upload', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      lastModified: file.lastModified
    });

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      photoServiceLogger.warn('File validation failed - invalid type', {
        fileName: file.name,
        fileType: file.type,
        allowedTypes
      });
      return {
        valid: false,
        error: 'Only JPEG, PNG, and WebP images are allowed'
      };
    }

    const maxSize = 10 * 1024 * 1024; // 10MB (increased from 5MB)
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      photoServiceLogger.warn('File validation failed - size too large', {
        fileName: file.name,
        fileSize: file.size,
        fileSizeMB,
        maxSizeMB: '10'
      });
      return {
        valid: false,
        error: `File size (${fileSizeMB}MB) exceeds the 10MB limit. Please compress the image or use a smaller file.`
      };
    }

    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    photoServiceLogger.info('File validation passed', {
      fileName: file.name,
      fileSize: file.size,
      fileSizeMB,
      fileType: file.type
    });
    
    return { valid: true };
  }

  /**
   * Upload photo to S3 (real implementation) - Returns string URL to match PhotoUpload expectations
   */
  async uploadPhoto(
    file: File, 
    itemId: string,
    onProgress?: (progress: PhotoUploadProgress) => void
  ): Promise<string> {
    const timer = logger.startTimer('PhotoService', 'uploadPhoto');
    
    try {
      photoServiceLogger.info('Starting photo upload process', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        itemId,
        tenantId: this.tenantId,
        timestamp: new Date().toISOString()
      });
      
      // Get presigned URL
      const { uploadUrl, photoKey, publicUrl } = await this.getPresignedUploadUrl(
        itemId,
        file.name,
        file.type
      );

      photoServiceLogger.debug('Upload URLs obtained', {
        fileName: file.name,
        itemId,
        hasUploadUrl: !!uploadUrl,
        hasPublicUrl: !!publicUrl,
        photoKey
      });

      // Upload file to S3
      const uploadResult = await this.uploadToS3(file, uploadUrl, onProgress);
      
      if (uploadResult.success) {
        photoServiceLogger.info('Photo upload completed successfully', {
          fileName: file.name,
          fileSize: file.size,
          itemId,
          photoKey,
          publicUrl: publicUrl.substring(0, 50) + '...', // Truncate URL for logging
        });
        
        timer();
        return publicUrl; // Return URL string directly
      } else {
        timer();
        throw new Error(uploadResult.error || 'Upload failed');
      }
    } catch (error: any) {
      timer();
      photoServiceLogger.error('Photo upload process failed', {
        fileName: file.name,
        fileSize: file.size,
        itemId,
        tenantId: this.tenantId,
        error: error.message
      }, error);
      throw error; // Let PhotoUpload handle the error
    }
  }

  /**
   * Delete photo from S3
   */
  async deletePhoto(photoKey: string): Promise<{ success: boolean; error?: string }> {
    const timer = logger.startTimer('PhotoService', 'deletePhoto');
    
    try {
      photoServiceLogger.info('Starting photo deletion', {
        photoKey: photoKey.substring(0, 50) + '...', // Truncate key for logging
        tenantId: this.tenantId,
        timestamp: new Date().toISOString()
      });
      
      const response = await fetch(`${API_BASE_URL}/photos/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          photoKey,
          tenantId: this.tenantId
        })
      });

      photoServiceLogger.info('Photo deletion API response', {
        photoKey: photoKey.substring(0, 50) + '...',
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (!response.ok) {
        const errorText = await response.text();
        photoServiceLogger.error('Photo deletion failed', {
          photoKey: photoKey.substring(0, 50) + '...',
          status: response.status,
          statusText: response.statusText,
          errorText: errorText.substring(0, 500)
        });
        
        timer();
        throw new Error('Failed to delete photo');
      }

      const data = await response.json();
      photoServiceLogger.info('Photo deleted successfully', {
        photoKey: photoKey.substring(0, 50) + '...',
        success: data.success
      });
      
      timer();
      return { success: data.success };
    } catch (error: any) {
      timer();
      photoServiceLogger.error('Photo deletion error', {
        photoKey: photoKey.substring(0, 50) + '...',
        tenantId: this.tenantId,
        error: error.message
      }, error);
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get photo metadata (if needed for future functionality)
   */
  async getPhotoMetadata(photoKey: string): Promise<{ success: boolean; metadata?: any; error?: string }> {
    const timer = logger.startTimer('PhotoService', 'getPhotoMetadata');
    
    try {
      photoServiceLogger.debug('Fetching photo metadata', {
        photoKey: photoKey.substring(0, 50) + '...',
        tenantId: this.tenantId
      });
      
      // Implementation would depend on your API
      // This is a placeholder for future functionality
      photoServiceLogger.debug('Photo metadata operation completed (placeholder)');
      
      timer();
      return { success: true, metadata: {} };
    } catch (error: any) {
      timer();
      photoServiceLogger.error('Photo metadata fetch error', {
        photoKey: photoKey.substring(0, 50) + '...',
        error: error.message
      }, error);
      
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Set tenant ID for multi-tenant operations
   */
  setTenantId(newTenantId: string): void {
    photoServiceLogger.info('Updating tenant ID', {
      previousTenantId: this.tenantId,
      newTenantId
    });
    this.tenantId = newTenantId;
  }

  /**
   * Get current tenant ID
   */
  getTenantId(): string {
    return this.tenantId;
  }

  /**
   * Get supported file types
   */
  getSupportedFileTypes(): string[] {
    return ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  }

  /**
   * Get maximum file size in bytes
   */
  getMaxFileSize(): number {
    return 10 * 1024 * 1024; // 10MB
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

// Export singleton instance
export const photoService = new PhotoService();
